import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Calendar, User, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
};

export default function WhyWebsite() {
  useEffect(() => {
    // Scroll to top on load since this is a new page
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
      
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 relative z-10">
        
        {/* Back Link */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <Link to="/blogs" className="inline-flex items-center text-sm font-body text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blogs
          </Link>
        </motion.div>

        {/* Header Section */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 text-white font-body border border-white/5">
              Business Growth
            </span>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <Calendar className="w-3.5 h-3.5" />
              April 20, 2026
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <User className="w-3.5 h-3.5" />
              EsyAI Team
            </div>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] mb-6">
            Why Your Business Needs a Professional Website in 2025
          </h1>
          
          <p className="text-xl text-white/60 font-body font-light leading-relaxed">
            Discover why having a professional website is essential for every UK business in 2025, from credibility and SEO to 24/7 sales and customer trust.
          </p>
        </motion.div>

        {/* Cover Image */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden mb-16 border border-white/10 liquid-glass shadow-2xl relative"
        >
          <img 
            src="/blogs/blog-1-new.png" 
            alt="Professional Website Concept" 
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        </motion.div>

        {/* Article Content */}
        <motion.article 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="prose prose-invert prose-lg max-w-none font-body text-white/70 prose-headings:font-heading prose-headings:text-white prose-a:text-purple-400 hover:prose-a:text-purple-300 prose-p:leading-loose"
        >
          <p className="lead text-xl text-white/90">
            If you're running a business without a website, you're invisible to millions of potential customers searching online right now. In 2025, a professional website is no longer a luxury. It's the single most important digital asset your business can own.
          </p>
          
          <p>
            At EsyAI, we design and develop high-performance websites for businesses across the UK, powered by AI and built to convert. In this post, we break down exactly why having a professional website is critical for your growth.
          </p>

          <h2 className="text-2xl mt-12 mb-6">1. Your Website Works 24/7, Even When You Don't</h2>
          <p>
            Unlike a physical shopfront or a member of staff, your website never clocks off. It answers questions, showcases your services, and captures leads around the clock, whether it's 3pm on a Tuesday or 3am on a Sunday.
          </p>
          <p>
            Businesses with professional websites generate leads passively, without lifting a finger. Your website is your hardest-working team member.
          </p>

          <h2 className="text-2xl mt-12 mb-6">2. Credibility and Trust</h2>
          <p>
            Over 80% of consumers research a company online before making a purchasing decision. If you don't have a website, or your site looks outdated, potential customers will simply move on to a competitor who does.
          </p>
          <p>A polished, professional website instantly communicates:</p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>You are a legitimate, established business</li>
            <li>You care about the customer experience</li>
            <li>You are invested in your brand and reputation</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">3. SEO: Be Found When It Matters Most</h2>
          <p>
            Search Engine Optimisation (SEO) is the art of ranking your business on Google when customers are actively searching for your products or services. Without a website, you cannot exist in search results.
          </p>
          <p>
            A well-structured website with targeted content can drive consistent, free organic traffic to your business every single day. EsyAI builds websites with SEO baked in from day one, from site architecture to page speed to schema markup.
          </p>

          <h2 className="text-2xl mt-12 mb-6">4. Showcase Your Products and Services</h2>
          <p>
            Your website is your digital brochure, but far more powerful. You can showcase every product, explain every service, display pricing, share testimonials, and answer FAQs. All in one place, accessible instantly from any device.
          </p>

          <h2 className="text-2xl mt-12 mb-6">5. Compete with Larger Businesses</h2>
          <p>
            A professionally designed website levels the playing field. A small business with an outstanding website can outperform a large competitor with a poor one. Your website is where first impressions are made, and first impressions win customers.
          </p>

          <h2 className="text-2xl mt-12 mb-6">6. Data and Analytics</h2>
          <p>
            With a website, you gain access to powerful analytics that tell you exactly who is visiting your site, how they found you, which pages they viewed, and where they dropped off. This data is gold for making smarter business decisions.
          </p>

          <h2 className="text-2xl mt-12 mb-6">7. Integration with Your Marketing Channels</h2>
          <p>
            Your website is the hub of all your digital marketing. Every social media post, paid ad, email campaign, and Google listing should drive traffic back to your website, where you control the experience and the conversion.
          </p>

          <hr className="my-12 border-white/10" />

          <h2 className="text-2xl mb-6">Final Thoughts</h2>
          <p>
            Whether you're a sole trader, a growing SME, or an established brand, investing in a professional website is one of the highest-ROI decisions you can make. Your competitors already have one. Don't let them win your next customer.
          </p>

          {/* Call to Action block */}
          <div className="not-prose mt-12 p-8 liquid-glass rounded-3xl border border-white/10 bg-gradient-to-br from-purple-900/20 to-transparent">
            <h3 className="text-xl font-heading text-white mb-3">Ready to upgrade your digital presence?</h3>
            <p className="text-white/70 mb-6 text-base font-body">
              EsyAI builds fast, beautiful, conversion-optimised websites for businesses across the UK. Let's create something extraordinary.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium text-sm rounded-full hover:bg-white/90 transition-colors font-body"
            >
              Get Started <ArrowUpRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

        </motion.article>
      </div>
    </div>
  );
}
